import { useNotification } from 'hooks/ui/useNotification';
import { useHistory } from 'react-router-dom';
import api from 'lib/trpcClient';
import { TRPCClientErrorLike } from '@trpc/client';
import { ApiRouter } from 'further-api/trpc/routes';
import { useQueryClient, QueryKey } from '@tanstack/react-query';

export const handleTrpcError = (
  errorNotification: (message: string) => void,
) => {
  return (err: TRPCClientErrorLike<ApiRouter>) => {
    errorNotification(
      err?.data?.cause ?? err?.message ?? 'Something went wrong.',
    );
  };
};

const useHandleTrpcMutation = <
  TData = unknown,
  TInput = unknown,
  TContext = unknown,
>() => {
  const { error, success } = useNotification();
  const history = useHistory();
  const apiUtils = api.useUtils();
  const queryClient = useQueryClient();

  type Args = {
    successMessage?: string;
    refetchType?: 'none' | 'all' | 'active' | 'inactive';
    successRedirect?: string;
    invalidations?: Array<QueryKey>;
    evictions?: Array<QueryKey>;
    onSuccess?: (data: TData, variables: TInput) => void;
    onError?: (
      err: TRPCClientErrorLike<ApiRouter>,
      variables: TInput,
      context: TContext | undefined,
    ) => void;
    onMutate?: (input: TInput) => TContext | Promise<TContext>;
    onSettled?: (
      data: TData | undefined,
      error: TRPCClientErrorLike<ApiRouter> | null,
      input: TInput,
      context: TContext | undefined,
    ) => void;
  };

  return ({
    successMessage,
    successRedirect,
    onSuccess,
    onError,
    onMutate,
    onSettled,
    refetchType = 'all',
    invalidations,
    evictions,
  }: Args) => ({
    onSuccess: async (data: TData, variables: TInput) => {
      if (successMessage) {
        success(successMessage);
      }

      if (evictions) {
        evictions.forEach((eviction) => {
          queryClient.removeQueries({ queryKey: eviction });
        });
      }

      // if there are no defined invalidations, then invalidate ALL queries in the cache
      if (!invalidations) {
        await apiUtils.invalidate(undefined, { refetchType });
      } else {
        // otherwise, invalidate only the defined queries
        await Promise.all(
          invalidations.map((invalidation) => {
            return queryClient.invalidateQueries({ queryKey: invalidation });
          }),
        );
      }

      onSuccess?.(data, variables);

      if (successRedirect) {
        history.push(successRedirect);
      }
    },
    onError: (
      err: TRPCClientErrorLike<ApiRouter>,
      variables: TInput,
      context: TContext | undefined,
    ) => {
      onError?.(err, variables, context);
      handleTrpcError(error)(err);
    },
    onMutate: (input: TInput) => {
      return onMutate?.(input);
    },
    onSettled: (
      data: TData | undefined,
      err: TRPCClientErrorLike<ApiRouter> | null,
      input: TInput,
      context: TContext | undefined,
    ) => {
      onSettled?.(data, err, input, context);
    },
  });
};

export default useHandleTrpcMutation;
